import {
  DirectionsRenderer,
  DirectionsService,
  Libraries,
  GoogleMap,
} from '@react-google-maps/api'

import * as React from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import AutosuggestHighlightParse from "autosuggest-highlight/parse";
import { debounce } from '@mui/material/utils';
import { Chip } from '@mui/material';
import { CircularProgressCentered } from './sharedcomponents';

// This key was created specifically for the demo in mui.com.
// You need to create a new one for your application.
const GOOGLE_MAPS_API_KEY = 'AIzaSyC3aviU6KHXAjoSnxcw6qbOhjnFctbxPkE';
export const goggleMapsApiKey = "AIzaSyDvqapzpG_NMGMUv9Nj9IJnLNasbt_XzR8"

function loadScript(src: string, position: HTMLElement | null, id: string) {
  if (!position) {
    return;
  }

  const script = document.createElement('script');
  script.setAttribute('async', '');
  script.setAttribute('id', id);
  script.src = src;
  position.appendChild(script);
}

const autocompleteService = { current: null };

interface MainTextMatchedSubstrings {
  offset: number;
  length: number;
}
interface StructuredFormatting {
  main_text: string;
  secondary_text: string;
  main_text_matched_substrings?: readonly MainTextMatchedSubstrings[];
}
export interface PlaceType {
  description: string;
  place_id?: string,
  structured_formatting?: StructuredFormatting;
}

interface LocationsAutocompleteProps {
  //defaultInputValue?: string;
  value: PlaceType[];
  onChange: (value: PlaceType[]) => void;
  countryCode?: string;
  [key: string]: any; // To allow for additional props
}

//export const LocationsAutocomlete: React.FC<LocationsAutocompleteProps> = (props) => {
export const LocationsMultipleAutocomplete = React.forwardRef<
  any, // The type of the ref (you can replace `any` with a specific type if needed)
  LocationsAutocompleteProps
>((props, ref) => {
  const { value, onChange, countryCode, helperText, label, error, ...restProps } = props


  //const [value, setValue] = React.useState<PlaceType[] | undefined>(undefined);
  const [inputClear, setInputClear] = React.useState(false);
  const [inputValue, setInputValue] = React.useState('');
  const [options, setOptions] = React.useState<readonly PlaceType[]>([]);
  const [optionsState, setOptionsState] = React.useState<{ isUnInitialized: boolean, isLoadng: boolean, isSuccess: boolean }>({ isUnInitialized: true, isLoadng: false, isSuccess: false });
  const loaded = React.useRef(false);

  if (typeof window !== 'undefined' && !loaded.current) {
    // if (!document.querySelector('#google-maps')) {
    //   loadScript(
    //     `https://maps.googleapis.com/maps/api/js?key=${goggleMapsApiKey}&libraries=places`,
    //     document.querySelector('head'),
    //     'google-maps',
    //   );
    // }

    loaded.current = true;
  }
  React.useImperativeHandle(ref, () => ({
    focus: () => {
      inputRef.current?.focus();
    },
    resetAndFocus() {
      setOptionsState({ isUnInitialized: true, isLoadng: false, isSuccess: false })
      setOptions([]); // Clear the selected value
      setInputValue(''); // Reset the input value      
      onChange([]); // Notify the parent that the value has been reset
      if (inputRef.current) {
        inputRef.current.focus(); // Focus the input element
      }
    }
  }));

  const readOnly =  value.length > 0 && !inputClear
  React.useEffect(() => {
    if (!readOnly && inputRef.current) {
      inputRef.current.blur();
      inputRef.current.disabled = true; // Temporarily disable the input
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.disabled = false; // Re-enable the input
          const touchEvent = new Event('touchstart', { bubbles: true, cancelable: true });
          inputRef.current.dispatchEvent(touchEvent); // Simulate a touch event
          inputRef.current.focus();
        }
      }, 300); // Small delay to ensure proper focus
    }
  }, [readOnly]);


  const fetch = React.useMemo(
    () =>
      debounce(
        (
          request: { input: string },
          callback: (results?: readonly PlaceType[]) => void,
        ) => {
          (autocompleteService.current as any).getPlacePredictions(
            {
              ...request, types: ['(regions)'], // ['geocode'], 
              componentRestrictions: { country: countryCode }, // Optionally restrict to a specific country (e.g., US)
              strictBounds: false,
            },
            callback,
          );
        },
        400,
      ),
    [countryCode],
  );

  React.useEffect(() => {

    let active = true;

    if (!autocompleteService.current && (window as any).google) {
      autocompleteService.current = new (
        window as any
      ).google.maps.places.AutocompleteService();
    }
    if (!autocompleteService.current) {
      return undefined;
    }

    if (inputValue === '') {
      setOptions([])//value ? value : []);
      return undefined;
    }
    setOptionsState({ ...optionsState, isLoadng: true, isSuccess: false, isUnInitialized: false })
    fetch({ input: inputValue }, (results?: readonly PlaceType[]) => {
      if (active) {
        let newOptions: readonly PlaceType[] = [];
        const resultsNotAlreadPicked = results?.filter((res:PlaceType) => !(value?.filter((v: any) => v.description === res.description)?.length))
        if (results) {
          newOptions = [...resultsNotAlreadPicked ?? []]// [...newOptions, ...results];
        }
        setOptions(newOptions);
        setOptionsState({ ...optionsState, isLoadng: false, isSuccess: true })
      }
      setOptionsState({ ...optionsState, isLoadng: false, isSuccess: true  })
    });

    return () => {
      active = false;
    };
  }, [value, inputValue, fetch]);
  //noOptionsText={optionsState.isUnInitialized ? "Please type in one or more first letters of a state/city you lived in" : optionsState.isLoadng ? "Looking for locations..." : `No matching Location found for ${inputValue}`}
  const shouldOpen = inputValue !== '' && options.length > 0 && value.length === 0;
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  return (    
    <>{optionsState.isLoadng && <CircularProgressCentered />}
      <Autocomplete
        {...restProps}
        fullWidth
        multiple
        id="google-map-demo"
        getOptionLabel={(option) =>
          typeof option === 'string' ? option : option.description
        }
        filterOptions={(x) => x}
        options={options}
        autoComplete
        includeInputInList
        filterSelectedOptions
        value={value}
        open={shouldOpen}
        onChange={(event: any, newValue: PlaceType[] | null) => {
          if (newValue) {
            onChange(newValue)
            if (newValue.length === 0) {
              setInputClear( true)
              setOptions([])
              setOptionsState({ isUnInitialized: true, isLoadng: false, isSuccess: false })
              if (inputRef.current) {
                inputRef.current.focus(); // Focus the input element
                // inputRef.current.value = "f"
                // inputRef.current.dispatchEvent(new Event('input', { bubbles: true })); // Trigger input event to notify React about the change
                // setInputValue('dd');
 
              }
            } else {
              setInputClear( false)
            }
          }
        }}
        onInputChange={(event, newInputValue) => {
          newInputValue = newInputValue.trim()
          //if (newInputValue) {
            setInputValue(newInputValue);
          //}
        }}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip variant="outlined" label={option.description} {...getTagProps({ index })} />
          ))
        }
        renderInput={(params) => {
          const  locationHelperText=  value?.length > 0 ? `Click X to choose a different location` : (inputValue && optionsState.isSuccess && options.length === 0 && value.length === 0) ? `No matching Location found for ${inputValue}`: helperText   
          const locationError=  optionsState.isSuccess && options.length === 0 && inputValue.length > 0 ? true : undefined
          console.log( optionsState.isSuccess)         
         return (          
          <TextField {...params} type="text"  inputRef={inputRef}  inputProps={{ ...params.inputProps,   readOnly: value.length > 0 && !inputClear}}
            variant="outlined"
            //label={value.length === 0 ? "Please type in zip/postal code or city/town you live in" : "Click X to choose a different location"}
            label={ label }
            error={locationError || !!error} 
            helperText={ locationHelperText} 
            fullWidth />
        )}}
        noOptionsText=""
        renderOption={(props, option) => {
          const matches =
            option.structured_formatting?.main_text_matched_substrings || [];

          const parts = AutosuggestHighlightParse(
            option.structured_formatting?.main_text ?? '',
            matches.map((match: any) => [match.offset, match.offset + match.length]),
          );

          return (
            <li {...props}>
              <Grid container alignItems="center">
                <Grid  sx={{ display: 'flex', width: 44 }}>
                  <LocationOnIcon sx={{ color: 'text.secondary' }} />
                </Grid>
                <Grid  sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}>
                  {parts.map((part: any, index: number) => (
                    <Box
                      key={index}
                      component="span"
                      sx={{ fontWeight: part.highlight ? 'bold' : 'regular' }}
                    >
                      {part.text}
                    </Box>
                  ))}
                  <Typography variant="body2" color="text.secondary">
                    {option.structured_formatting?.secondary_text}
                  </Typography>
                </Grid>
              </Grid>
            </li>
          );
        }}
      />
 </>     );
})
